import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import { getContentStackData } from 'features/press/detail/actions';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import Container from 'components/Container';
import ContentTypography from 'components/ContentTypography';
import {
  PageHeaderGrid,
  PageHeaderText,
  PageHeaderBackButton
} from 'components/PageHeader';
import { setTopNavColorDark } from 'features/navigation/header/actions';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { ResponsiveFeatureImg } from 'components/ResponsiveImg';
import ArrowLeftIcon from 'mdi-material-ui/ArrowLeft';
import MetaTags from 'components/MetaTags';
import SwiftypeTags from 'components/SwiftypeTags';
import StructuredData from 'components/StructuredData';

const PressDivider = styled(Divider)`
  margin-bottom: 40px;
`;

const PaddedDivider = styled(Divider)`
  margin-bottom: 5vh;
  margin-top: 5vh;
`;

const HeroImg = styled(ResponsiveFeatureImg)`
  margin-bottom: 40px;
  height: 0;
  overflow: visible;
  padding-bottom: 56.25%;
`;

const PressContent = styled.div`
  margin-bottom: 5vh;
  margin-left: auto;
  margin-right: auto;
  width: 80%;
`;

const AdditionalTitleTypography = styled(Typography)`
  margin: 1em 0;
`;

const AdditionalInfo = ({
  companyTitle,
  companyAbout,
  contactTitle,
  contactAbout
}) => {
  return (
    <Grid container spacing={40}>
      <Grid item xs={12} sm={8}>
        <AdditionalTitleTypography variant="overline">
          {companyTitle}
        </AdditionalTitleTypography>
        <ContentTypography
          dangerouslySetInnerHTML={{
            __html: companyAbout
          }}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <AdditionalTitleTypography variant="overline">
          {contactTitle}
        </AdditionalTitleTypography>
        <ContentTypography
          dangerouslySetInnerHTML={{
            __html: contactAbout
          }}
        />
      </Grid>
    </Grid>
  );
};

export class PressDetail extends React.PureComponent {
  componentWillMount() {
    this.props.setTopNavColorDark();
  }

  componentDidMount() {
    this.props.getContentStackData(this.props.match.url);
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevProps.match.url !== this.props.match.url) {
      this.props.getContentStackData(this.props.match.url);
    }
  }

  render() {
    const { stack, loading } = this.props;

    return !loading ? (
      <>
        <MetaTags stack={stack} />
        <SwiftypeTags
          stack={stack}
          title={stack.headline}
          excerpt={stack.subtitle}
          publishedAt={stack.date}
          contentType="Press Release"
          image={stack?.image?.url}
        />
        <StructuredData
          type="NewsArticle"
          headline={stack.headline}
          description={stack.subtitle}
          image={stack?.image?.url}
          datePublished={stack.date}
        />

        <Container>
          <PageHeaderGrid>
            <Grid item xs={12}>
              <PageHeaderBackButton to="/press" component={Link}>
                <ArrowLeftIcon /> All Press Releases
              </PageHeaderBackButton>
              <PageHeaderText
                heading={stack.headline}
                subheading={stack.subtitle}
              />
            </Grid>
          </PageHeaderGrid>

          <Grid container>
            <Grid item xs={12}>
              <PressContent>
                {stack.image ? (
                  <HeroImg src={stack.image.url} alt="lead image" />
                ) : (
                  <PressDivider />
                )}
                <Typography variant="overline">
                  {stack.date &&
                    dayjs(stack.date, 'YYYY-MM-DD').format('MMM DD')}
                </Typography>

                <ContentTypography
                  variant="body2"
                  component="div"
                  dangerouslySetInnerHTML={{ __html: stack.content }}
                  data-swiftype-name="body"
                  data-swiftype-type="text"
                />
                <Grid container spacing={40}>
                  {stack.reference.length > 0 && (
                    <div>
                      <PaddedDivider />
                      <AdditionalInfo
                        companyTitle={
                          stack.reference[0].about_the_company.about_synthego
                        }
                        companyAbout={
                          stack.reference[0].about_the_company.description
                        }
                        contactTitle={
                          stack.reference[0].media_contact.media_contact_name
                        }
                        contactAbout={
                          stack.reference[0].media_contact.media_contact_details
                        }
                      />
                    </div>
                  )}
                  {stack.other_companies && stack.other_companies.length > 0 && (
                    <div>
                      <PaddedDivider />
                      {stack.other_companies.map((company, idx) => {
                        return (
                          <AdditionalInfo
                            key={idx}
                            companyTitle={company.about.title}
                            companyAbout={company.about.description}
                            contactTitle={company.contact.title}
                            contactAbout={company.contact.details}
                          />
                        );
                      })}
                    </div>
                  )}
                </Grid>
              </PressContent>
            </Grid>
          </Grid>
        </Container>
      </>
    ) : null;
  }
}

PressDetail.propTypes = {
  stack: PropTypes.shape({
    headline: PropTypes.string,
    subtitle: PropTypes.string,
    date: PropTypes.string,
    image: PropTypes.shape({
      url: PropTypes.string
    }),
    other_companies: PropTypes.array,
    reference: PropTypes.array
  })
};

export default connect(
  state => ({
    stack: state.press_detail.stack,
    loading: state.press_detail.loading
  }),
  { getContentStackData, setTopNavColorDark }
)(PressDetail);
